import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlatform, HEADER_FONTS, BODY_FONTS } from "../context/PlatformContext";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Save, LogOut } from "lucide-react";
import { useConfirm } from "../components/ConfirmProvider";

export default function SuperAdminSettings() {
  const { logout } = useAuth();
  const { settings: platformSettings, refresh } = usePlatform();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/superadmin/settings");
      setS(data.settings);
    })();
  }, []);

  const upd = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/superadmin/settings", {
        company_name: s.company_name,
        tagline: s.tagline,
        address: s.address,
        contact_email: s.contact_email,
        header_font: s.header_font,
        body_font: s.body_font,
      });
      await refresh();
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_000_000) { toast.error("Max 2MB please"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/superadmin/settings/logo", { data_url: dataUrl });
      setS((prev) => ({ ...prev, logo_url: data.logo_url }));
      await refresh();
      toast.success("Logo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    const ok = await confirm({
      title: "Remove logo?",
      message: "You'll fall back to the default initial badge until a new logo is uploaded.",
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete("/superadmin/settings/logo");
      setS((prev) => ({ ...prev, logo_url: null }));
      await refresh();
      toast.success("Logo removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!s) return <div className="min-h-screen flex items-center justify-center text-[#7A7571]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#EBE5D9]">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 lg:px-12 py-4">
          <Link to="/admin" className="inline-flex items-center gap-2 text-[#7A7571] hover:text-[#2C2A29]">
            <ArrowLeft size={16} /> Console
          </Link>
          <button onClick={doLogout} data-testid="logout-btn" className="btn-ghost !p-2"><LogOut size={16} /></button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Platform</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#2C2A29] mt-2">Settings</h1>
          <p className="text-[#7A7571] mt-2">These changes affect the entire platform (landing page, sign-in, sidebars).</p>
        </div>

        {/* Logo */}
        <section className="card-soft">
          <SectionHeading>Brand logo</SectionHeading>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-24 h-24 rounded-2xl bg-[#F7F4EC] flex items-center justify-center overflow-hidden border border-[#EBE5D9] flex-shrink-0">
              {s.logo_url ? (
                <img src={s.logo_url} alt="Logo" className="w-full h-full object-cover" data-testid="settings-logo-preview" />
              ) : (
                <span className="font-display text-3xl font-bold text-[#7A7571]">{(s.company_name || "R").slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#7A7571] mb-3">PNG, JPG, or SVG. Square works best. Max 2MB.</p>
              <div className="flex flex-wrap gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="settings-logo-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="settings-logo-upload-btn"
                  className="btn-primary inline-flex items-center gap-2 !py-2 !px-4 text-sm">
                  <Upload size={14} /> {uploading ? "Uploading…" : s.logo_url ? "Replace logo" : "Upload logo"}
                </button>
                {s.logo_url && (
                  <button type="button" onClick={removeLogo}
                    data-testid="settings-logo-remove-btn"
                    className="btn-secondary inline-flex items-center gap-2 !py-2 !px-4 text-sm">
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Text fields */}
        <form onSubmit={save} className="space-y-6">
          <section className="card-soft space-y-4">
            <SectionHeading>Identity</SectionHeading>
            <Field label="Company name" testId="settings-company-name" value={s.company_name} onChange={(v) => upd("company_name", v)} placeholder="OrkaiFlow" />
            <Field label="Tagline" testId="settings-tagline" value={s.tagline} onChange={(v) => upd("tagline", v)} placeholder="Renovation projects, under one calm roof." />
          </section>

          <section className="card-soft space-y-4">
            <SectionHeading>Contact</SectionHeading>
            <Field label="Company address" testId="settings-address" value={s.address} onChange={(v) => upd("address", v)} placeholder="1 Marina Boulevard, Singapore 018989" required={false} multiline />
            <Field label="Contact email" testId="settings-contact-email" type="email" value={s.contact_email} onChange={(v) => upd("contact_email", v)} placeholder="hello@yourdomain.com" required={false} />
          </section>

          <section className="card-soft space-y-5">
            <SectionHeading>Typography</SectionHeading>
            <FontPicker label="Header font" testId="settings-header-font" value={s.header_font} options={HEADER_FONTS} onChange={(v) => upd("header_font", v)} />
            <FontPicker label="Body font" testId="settings-body-font" value={s.body_font} options={BODY_FONTS} onChange={(v) => upd("body_font", v)} />
            <div className="card-soft !p-5 !bg-[#F7F4EC] !border-none">
              <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-3">Live preview</div>
              <div className="text-3xl font-bold" style={{ fontFamily: `'${s.header_font}', system-ui, sans-serif`, letterSpacing: "-0.02em" }}>
                {s.company_name || "OrkaiFlow"}
              </div>
              <div className="mt-2 text-base" style={{ fontFamily: `'${s.body_font}', system-ui, sans-serif` }}>
                {s.tagline || "Renovation projects, under one calm roof."}
              </div>
            </div>
          </section>

          <div className="sticky bottom-4 flex justify-end">
            <button data-testid="settings-save-btn" disabled={saving} className="btn-primary inline-flex items-center gap-2">
              <Save size={16} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function SectionHeading({ children }) {
  return <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{children}</div>;
}

function Field({ label, testId, value, onChange, type = "text", placeholder, required = true, multiline }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">{label}</label>
      {multiline ? (
        <textarea data-testid={testId} required={required} value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="input-soft min-h-[80px]" placeholder={placeholder} />
      ) : (
        <input data-testid={testId} type={type} required={required} value={value || ""}
          onChange={(e) => onChange(e.target.value)} className="input-soft" placeholder={placeholder} />
      )}
    </div>
  );
}

function FontPicker({ label, testId, value, options, onChange }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">{label}</label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {options.map((f) => (
          <button
            key={f} type="button" onClick={() => onChange(f)}
            data-testid={`${testId}-${f.replace(/\s+/g, "-").toLowerCase()}`}
            className={`text-left px-4 py-3 rounded-2xl border transition-all ${value === f ? "border-[#E07A5F] bg-[#FFF6F1]" : "border-[#EBE5D9] bg-white hover:border-[#E07A5F]/40"}`}
            style={{ fontFamily: `'${f}', system-ui, sans-serif` }}
          >
            <div className="text-base font-semibold text-[#2C2A29]">{f}</div>
            <div className="text-xs text-[#7A7571] mt-0.5">Aa Bb Cc 123</div>
          </button>
        ))}
      </div>
    </div>
  );
}
